import React, { useEffect, useState } from "react";
import { BarChart, Bar, PieChart, Pie, Cell, Tooltip, XAxis, YAxis, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import api, { fmtJMD } from "../../lib/api";

const COLORS = ["#F74B64", "#FFA62B", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [sales, setSales] = useState(null);
  const [salesRange, setSalesRange] = useState(30);
  useEffect(() => { api.get("/analytics/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get(`/admin/analytics/sales?days=${salesRange}`).then((r) => setSales(r.data)).catch(() => {});
  }, [salesRange]);

  if (!data) return <div className="text-sm text-zinc-500">Loading analytics...</div>;
  const t = data.totals;

  return (
    <div data-testid="admin-dashboard">
      <div className="overline text-zinc-500 mb-2">Overview</div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-6 sm:mb-8">Business at a glance</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
        <Stat label="Total sales" value={fmtJMD(t.total_sales)} accent="#F74B64" testid="stat-total-sales" />
        <Stat label="Orders" value={t.total_orders} testid="stat-orders" />
        <Stat label="Delivery revenue" value={fmtJMD(t.delivery_revenue)} testid="stat-delivery-revenue" />
        <Stat label="Visits (last 30d)" value={t.total_visits} testid="stat-visits" />
        <Stat label="Preset box sales" value={fmtJMD(t.preset_box_revenue)} testid="stat-preset" />
        <Stat label="Custom box sales" value={fmtJMD(t.custom_box_revenue)} testid="stat-custom" />
        <Stat label="Low-stock items" value={t.low_stock_count} accent={t.low_stock_count > 0 ? "#F59E0B" : undefined} testid="stat-lowstock" />
        {sales && <Stat label="Repeat customer rate" value={`${sales.repeat_customer_rate}%`} testid="stat-repeat" />}
      </div>

      {/* Sales over time */}
      <Card title="Revenue trend" className="mb-6">
        <div className="flex flex-wrap gap-2 mb-4">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setSalesRange(d)}
                    className={`px-3 py-1 text-xs font-semibold rounded-full border ${
                      salesRange === d ? "bg-[#F74B64] text-white border-[#F74B64]" : "bg-white text-zinc-600 border-zinc-200"
                    }`}
                    data-testid={`sales-range-${d}`}>
              Last {d}d
            </button>
          ))}
          {sales && (
            <div className="ml-auto text-xs text-zinc-500 flex gap-4 flex-wrap">
              <span>Orders: <b className="text-zinc-900">{sales.total_orders}</b></span>
              <span>Revenue: <b className="text-zinc-900">{fmtJMD(sales.total_revenue)}</b></span>
              <span>AOV: <b className="text-zinc-900">{fmtJMD(sales.avg_order_value)}</b></span>
            </div>
          )}
        </div>
        {sales && sales.by_day.length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={sales.by_day}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmtJMD(v)} />
              <Line type="monotone" dataKey="revenue" stroke="#F74B64" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Empty />
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Best-selling flavours">
          {data.best_flavours.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.best_flavours}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="qty" fill="#F74B64" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Orders by parish">
          {data.orders_by_location.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.orders_by_location} dataKey="count" nameKey="parish" outerRadius={90} label>
                  {data.orders_by_location.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Top visited pages">
          {data.visits_by_path.length === 0 ? <Empty /> : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[300px]">
              <thead><tr className="text-zinc-500 text-xs"><th className="text-left pb-2">Path</th><th className="text-right pb-2">Visits</th></tr></thead>
              <tbody>
                {data.visits_by_path.map((v) => (
                  <tr key={v.path} className="border-t border-zinc-100">
                    <td className="py-2 font-mono text-xs break-all">{v.path}</td>
                    <td className="text-right font-semibold">{v.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </Card>

        <Card title="Product sales breakdown">
          {data.product_sales.length === 0 ? <Empty /> : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[400px]">
              <thead><tr className="text-zinc-500 text-xs"><th className="text-left pb-2">Product</th><th className="text-right pb-2">Qty</th><th className="text-right pb-2">Revenue</th></tr></thead>
              <tbody>
                {data.product_sales.map((p) => (
                  <tr key={p.name} className="border-t border-zinc-100">
                    <td className="py-2">{p.name}</td>
                    <td className="text-right">{p.qty}</td>
                    <td className="text-right font-semibold">{fmtJMD(p.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </Card>
      </div>

      <Card title="Recent orders" className="mt-6">
        {data.recent_orders.length === 0 ? <Empty /> : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead><tr className="text-zinc-500 text-xs">
              <th className="text-left pb-2">Order</th><th className="text-left pb-2">Customer</th>
              <th className="text-left pb-2">Parish</th><th className="text-left pb-2">Day</th>
              <th className="text-right pb-2">Total</th><th className="text-right pb-2">Status</th>
            </tr></thead>
            <tbody>
              {data.recent_orders.map((o) => (
                <tr key={o.id} className="border-t border-zinc-100">
                  <td className="py-2 font-mono text-xs">{o.id.slice(0, 8)}</td>
                  <td>{o.customer_name}</td>
                  <td>{o.parish}</td>
                  <td>{o.delivery_day}</td>
                  <td className="text-right font-semibold">{fmtJMD(o.total)}</td>
                  <td className="text-right"><span className="text-xs px-2 py-1 rounded-full bg-zinc-100">{o.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 sm:p-5" data-testid={testid}>
      <div className="overline text-zinc-500 truncate">{label}</div>
      <div className="font-display text-lg sm:text-2xl font-bold mt-2 break-words" style={accent ? { color: accent } : {}}>{value}</div>
    </div>
  );
}
function Card({ title, children, className = "" }) {
  return (
    <div className={`rounded-xl border border-zinc-200 bg-white p-5 sm:p-6 ${className}`}>
      <div className="font-display font-bold mb-4">{title}</div>
      {children}
    </div>
  );
}
function Empty() {
  return <div className="text-sm text-zinc-400 py-8 text-center">No data yet. Place some orders to see analytics.</div>;
}
